/**
 * SHARED STATUS COLOR MAPPING
 *
 * Single source of truth for execution-status colors across every report chart.
 * Predefined colors cover the common statuses; custom / user-defined statuses
 * fall through to a stable accent palette indexed by order-of-appearance.
 *
 * Uses Atlaskit design tokens with hex fallbacks so the colors track the
 * active theme (light / dark) when tokens are available.
 */

import { token } from '@atlaskit/tokens'

const STATUS_COLORS = {
  Pass: token('color.background.success.bold', '#22A06B'),
  Passed: token('color.background.success.bold', '#22A06B'),
  Fail: token('color.background.danger.bold', '#DE350B'),
  Failed: token('color.background.danger.bold', '#DE350B'),
  Blocked: token('color.icon.warning', '#E06C00'),
  Block: token('color.icon.warning', '#E06C00'),
  'In Progress': token('color.background.information.bold', '#0065FF'),
  'Not Run': token('color.background.neutral', '#DFE1E6'),
  Skip: token('color.background.neutral', '#DFE1E6'),
  Skipped: token('color.background.neutral', '#DFE1E6'),
  Query: token('color.background.information', '#DEEBFF'),
  Exclude: token('color.background.neutral.subtle', '#F4F5F7'),
  Excluded: token('color.background.neutral.subtle', '#F4F5F7')
}

const ACCENT_COLORS = [
  '#6554C0', // Purple
  '#00B8D9', // Teal
  '#FF5630', // Red-orange
  '#36B37E', // Green
  '#FFAB00', // Yellow
  '#403294', // Dark purple
  '#00A3BF', // Cyan
  '#BF2600'  // Dark red
]

/**
 * Resolve a color for a status name.
 *
 * @param {string} statusName
 * @param {number} index - index of the status among visible series; used to
 *   pick a stable accent color for custom statuses not in the preset map
 */
export const getStatusColor = (statusName, index = 0) => {
  if (!statusName) return ACCENT_COLORS[0]

  if (STATUS_COLORS[statusName]) return STATUS_COLORS[statusName]

  const normalized = statusName.toLowerCase()
  for (const [key, color] of Object.entries(STATUS_COLORS)) {
    if (key.toLowerCase() === normalized) return color
  }

  return ACCENT_COLORS[index % ACCENT_COLORS.length]
}
